import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { resolveInputOutline, resolveInputBoxShadow } from '../../styles-system/input-element-utils.esm.js';
import { isFlatDesign } from '../../themes/const.esm.js';
import { transitions } from '../../themes/theme-keys/transitions.esm.js';
import { getSafeTint } from '../../utils/colors.esm.js';
import { resolveColor } from '../../utils/styled-props.esm.js';

var switchPosition = {
  top: '-0.15em',
  on: '1.15em',
  off: '-0.4em',
  neutral: '0.4em'
};
var baseSwitchIconStyle = {
  content: '""',
  height: '1em',
  width: '1em',
  top: switchPosition.top,
  position: 'absolute',
  borderRadius: '100%',
  border: 'neutral.medium',
  borderImage: 'initial',
  transition: transitions.create('all', {
    duration: transitions.duration["short"]
  })
};
var standardHoverFocusBehaviors = function standardHoverFocusBehaviors(_ref) {
  var theme = _ref.theme,
    state = _ref.state,
    disabled = _ref.disabled;
  var isFlatDesign$1 = isFlatDesign(theme);
  return {
    focus: resolveInputOutline(theme, state ? state : isFlatDesign$1 ? 'primary.moderate' : 'primary', false, disabled, 4),
    hover: resolveInputOutline(theme, state ? state : isFlatDesign$1 ? 'black' : 'primary', true, disabled, 1)
  };
};
var defaultBehaviors = function defaultBehaviors(_ref2) {
  var theme = _ref2.theme,
    state = _ref2.state,
    disabled = _ref2.disabled,
    disableBg = _ref2.disableBg,
    disableColor = _ref2.disableColor;
  return _objectSpread2(_objectSpread2({}, standardHoverFocusBehaviors({
    theme: theme,
    state: state,
    disabled: disabled
  })), {}, {
    disabledNotCheck: _objectSpread2({}, resolveInputOutline(theme, disableBg ? disableBg : 'neutral.light')),
    disabledCheck: _objectSpread2(_objectSpread2({
      color: disableColor ? disableColor : 'neutral.dark'
    }, resolveInputOutline(theme, disableBg ? disableBg : 'neutral.light')), {}, {
      backgroundColor: resolveColor(theme, disableBg ? disableBg : 'neutral.light'),
      '&:hover': resolveInputOutline(theme, disableColor ? disableColor : 'neutral.light', true, disabled)
    })
  });
};
var checkbox = {
  "default": function _default(theme, props) {
    var state = props.state,
      disabled = props.disabled,
      indeterminate = props.indeterminate,
      disableBg = props.disableBg,
      disableColor = props.disableColor;
    var _defaultBehaviors = defaultBehaviors({
      theme: theme,
      state: state,
      disabled: disabled,
      indeterminate: indeterminate,
      disableBg: disableBg,
      disableColor: disableColor
    });
    // const isFlatDesign = _isFlatDesign(theme)
    return {
      '&:focus + div': _defaultBehaviors.focus,
      '&.is-active + div': _defaultBehaviors.focus,
      '&:hover:not([disabled]) + div': _defaultBehaviors.hover,
      '&.is-hover:not([disabled]) + div': _defaultBehaviors.hover,
      '&:checked + div': resolveInputOutline(theme, state ? state : 'primary'),
      '&:checked:focus + div': _defaultBehaviors.focus,
      '&:checked:focus:hover + div': resolveInputOutline(theme, state ? state : 'primary', true, disabled, 1),
      '&[disabled]:checked + div': _defaultBehaviors.disabledCheck,
      '&[disabled]:not([checked]) + div': _defaultBehaviors.disabledNotCheck,
      '&:disabled ~ span': {
        opacity: 0.6
      }
    };
  },
  primary: function primary(theme, props) {
    var state = props.state,
      disabled = props.disabled,
      indeterminate = props.indeterminate,
      disableBg = props.disableBg,
      disableColor = props.disableColor,
      iconColor = props.iconColor;
    var _defaultBehaviors = defaultBehaviors({
      theme: theme,
      state: state,
      disabled: disabled,
      indeterminate: indeterminate,
      disableBg: disableBg,
      disableColor: disableColor
    });
    var isFlatDesign$1 = isFlatDesign(theme);
    return {
      '& + div': {
        color: iconColor ? iconColor : 'white'
      },
      '&:indeterminate + div': {
        backgroundColor: resolveColor(theme, state ? state : 'primary')
      },
      '&:focus + div': _defaultBehaviors.focus,
      '&.is-active + div': _defaultBehaviors.focus,
      '&:hover:not([disabled]) + div:not([data-checked])': {
        border: 'neutrals.0',
        boxShadow: 'black.inset'
      },
      '&:is-hover:not([disabled]) + div:not([data-checked])': {
        border: 'neutrals.0',
        boxShadow: 'black.inset'
      },
      '&:checked': {
        '+ div': _objectSpread2(_objectSpread2({}, resolveInputOutline(theme, state ? state : 'primary')), {}, {
          backgroundColor: resolveColor(theme, state ? state : 'primary')
        }),
        ':focus + div': _defaultBehaviors.focus,
        ':hover:not([disabled]) + div': resolveInputOutline(theme, state ? state : isFlatDesign$1 ? 'black' : 'primary', false, disabled, 2)
      },
      '&:disabled': {
        '+ div': {
          cursor: 'not-allowed',
          ':not([data-checked])': _defaultBehaviors.disabledNotCheck
        },
        '.is-read-only + div + span': {
          cursor: 'auto'
        },
        ':not(.is-read-only) + div + span': {
          cursor: 'not-allowed'
        },
        ':not(.is-read-only) ~ span': {
          opacity: 0.6
        },
        ':checked + div': _defaultBehaviors.disabledCheck
      }
    };
  },
  "switch": function _switch(theme, props) {
    var state = props.state,
      disabled = props.disabled,
      indeterminate = props.indeterminate,
      iconBg = props.iconBg;
    var _standardHoverFocusBehaviors = standardHoverFocusBehaviors({
      theme: theme,
      state: state
    });
    return {
      '& + div': _objectSpread2(_objectSpread2({
        fontSize: '105%',
        margin: 'unset',
        alignSelf: 'center',
        backgroundColor: resolveColor(theme, 'neutrals.17'),
        height: '.95em',
        position: 'relative',
        width: '2em'
      }, resolveInputOutline(theme, 'neutral.light')), {}, {
        borderImage: 'initial',
        borderRadius: 'pill',
        '&::before': _objectSpread2(_objectSpread2({}, baseSwitchIconStyle), {}, {
          left: switchPosition.off,
          background: iconBg ? iconBg : 'white'
        })
      }),
      '&:indeterminate + div': {
        backgroundColor: resolveColor(theme, state ? state : 'primary'),
        '&::before': {
          left: switchPosition.neutral
        }
      },
      '&:focus + div': _standardHoverFocusBehaviors.focus,
      '&.is-active + div': _standardHoverFocusBehaviors.focus,
      '&:hover:not([disabled]) + div': _standardHoverFocusBehaviors.hover,
      '&.is-hover:not([disabled]) + div': _standardHoverFocusBehaviors.hover,
      '&:checked:not([disabled]) + div': _objectSpread2(_objectSpread2({}, resolveInputOutline(theme, state ? state : 'primary')), {}, {
        backgroundColor: resolveColor(theme, state ? state : 'primary'),
        '&::before': _objectSpread2({
          left: indeterminate ? switchPosition.neutral : switchPosition.on
        }, resolveInputOutline(theme, state ? state : 'primary')),
        '&:hover': resolveInputOutline(theme, state ? state : 'primary')
      }),
      '&:checked:focus + div': _standardHoverFocusBehaviors.focus,
      '&[disabled]:not([checked]) + div': _objectSpread2(_objectSpread2({}, resolveInputOutline(theme, 'neutral.light')), {}, {
        backgroundColor: resolveColor(theme, 'neutral.light'),
        '&:before': _objectSpread2({
          background: resolveColor(theme, 'neutral.light')
        }, resolveInputOutline(theme, 'neutral.light'))
      }),
      '&[disabled]:checked + div': {
        '&:hover': resolveInputOutline(theme, 'neutral', true, disabled),
        '&::before': _objectSpread2({
          left: indeterminate ? switchPosition.neutral : switchPosition.on,
          background: resolveColor(theme, 'neutral.light')
        }, resolveInputOutline(theme, 'neutral.light'))
      },
      '&:disabled ~ span': {
        opacity: 0.6
      }
    };
  },
  checkIcon: function checkIcon(theme, props) {
    var state = props.state;
    var isFlatDesign$1 = isFlatDesign(theme);
    return {
      '& + div': {
        borderRadius: '50%',
        mt: '.07em',
        color: getSafeTint(0.5, resolveColor(theme, state ? state : 'neutrals.5'))
      },
      '&:focus + div': _objectSpread2(_objectSpread2({}, isFlatDesign$1 ? {} : resolveInputBoxShadow(theme, state ? state : 'primary', false, 4)), {}, {
        color: resolveColor(theme, state ? state : isFlatDesign$1 ? 'primary.moderate' : 'primary')
      }),
      '&.is-active + div': _objectSpread2(_objectSpread2({}, resolveInputBoxShadow(theme, state ? state : 'primary', false, 4)), {}, {
        color: resolveColor(theme, state ? state : 'primary')
      }),
      '&:hover:not([disabled]) + div': {
        color: resolveColor(theme, state ? state : 'primary'),
        bg: 'neutral.light'
      },
      '&.is-hover:not([disabled]) + div': {
        color: resolveColor(theme, state ? state : 'primary'),
        bg: 'neutral.light'
      },
      '&:checked:focus + div': {
        color: resolveColor(theme, state ? state : isFlatDesign$1 ? 'primary.moderate' : 'primary')
      },
      '&[disabled]:not([checked]) + div': {
        opacity: '0.2',
        color: 'text',
        bg: 'unset'
      },
      '&[disabled]:checked + div': {
        opacity: '0.4'
      },
      '&:checked + div': {
        color: resolveColor(theme, state ? state : 'primary')
      },
      '&:disabled:not(.is-read-only) ~ span': {
        opacity: 0.6
      }
    };
  },
  //Temp variant until checkbox are reviewed
  checkIconSecondary: function checkIconSecondary(theme, props) {
    var state = props.state;
    var isFlatDesign$1 = isFlatDesign(theme);
    return {
      '& + div': _objectSpread2(_objectSpread2({
        borderRadius: '50%',
        mt: '.07em',
        color: getSafeTint(0.5, resolveColor(theme, state ? state : 'white'))
      }, resolveInputOutline(theme, 'white')), {}, {
        bg: 'secondary',
        borderWidth: '2px',
        p: '2px'
      }),
      '&.is-active + div': _objectSpread2(_objectSpread2({}, resolveInputBoxShadow(theme, state ? state : 'primary', false, 4)), {}, {
        color: resolveColor(theme, state ? state : 'white')
      }),
      '&:focus + div': _objectSpread2(_objectSpread2({}, isFlatDesign$1 ? {} : resolveInputBoxShadow(theme, state ? state : 'primary', false, 4)), {}, {
        color: resolveColor(theme, state ? state : 'white')
      }),
      '&:hover + div': {
        color: resolveColor(theme, state ? state : 'neutral.medium'),
        bg: 'unset'
      },
      '&.is-hover + div': {
        color: resolveColor(theme, state ? state : 'neutral.medium'),
        bg: 'unset'
      },
      '&:checked:focus + div': {
        color: resolveColor(theme, state ? state : 'white')
      },
      '&[disabled]:not([checked]) + div': {
        opacity: '0.2',
        color: 'text',
        bg: 'unset'
      },
      '&[disabled]:checked + div': {
        opacity: '0.4'
      },
      '&:checked + div': {
        color: resolveColor(theme, state ? state : 'white')
      },
      '&:disabled ~ span': {
        opacity: 0.6
      }
    };
  },
  classicSuite: function classicSuite(theme, props) {
    var state = props.state,
      disabled = props.disabled,
      indeterminate = props.indeterminate,
      disableBg = props.disableBg,
      disableColor = props.disableColor;
    var isFlatDesign$1 = isFlatDesign(theme);
    var _defaultBehaviors = defaultBehaviors({
      theme: theme,
      state: state,
      disabled: disabled,
      indeterminate: indeterminate,
      disableBg: disableBg,
      disableColor: disableColor
    });
    return {
      '+ div:after': {
        display: 'none',
        content: "' '",
        width: '0.2em',
        height: '0.5em',
        alignSelf: 'center',
        transform: 'translateY(-0.1em) rotate(45deg) skew(10deg)',
        border: 'white.thick',
        borderTop: 0,
        borderLeft: 0
      },
      '&:focus + div': {
        border: isFlatDesign$1 ? 'primary.moderate' : 'primary'
      },
      '&.is-active + div': {
        border: 'primary'
      },
      '&:hover + div': {
        border: 'primary'
      },
      '&.is-hover + div': {
        border: 'primary'
      },
      '&:checked + div': {
        backgroundColor: resolveColor(theme, state ? state : 'primary'),
        '&:hover': {
          border: 'primary'
        },
        ':after': {
          display: 'flex'
        }
      },
      '&:checked:focus + div': {
        border: isFlatDesign$1 ? 'primary.moderate' : 'primary'
      },
      '&[disabled]:checked + div': _defaultBehaviors.disabledCheck,
      '&[disabled]:not([checked]) + div': _defaultBehaviors.disabledNotCheck,
      '&:disabled ~ span': {
        opacity: 0.6
      }
    };
  }
};

export default checkbox;
//# sourceMappingURL=variants.esm.js.map
